// packages/utils/src/vibe-parser-v2.ts
// FRAME Vibe Engine v2 — elite at deciphering what the user wants
//
// Handles:
//   - Minimal input: "warm" → golden_hour archetype
//   - Maximum detail: "professional headshots with clean backgrounds, natural light,
//     no crowds, slight bokeh, authentic feel" → blends professional + editorial + candid
//   - Negation: "no crowds" → boosts detractor penalty, "skip group shots" → penalize faces
//   - Intensity modifiers: "very warm" → stronger weight shift than "warm"
//   - Synonym expansion: "bokeh" → depth, "vibrant" → scene interest, "candid" → authentic
//   - Emotional language: "moody" → low-key lighting + cool tones + high contrast
//   - Compound phrases: "golden hour at landmarks" → matches both golden_hour + travel

import type { VibeConfig, VibeArchetype, NovelWeightProfile } from '@frame/scoring-types';
import { DEFAULT_VIBE_CONFIG, VIBE_ARCHETYPES, NOVEL_MODE_WEIGHTS } from '@frame/scoring-types';

// ─── Extended vocabulary ────────────────────────────────────

interface ParsedIntent {
  archetypeMatches: Array<{ archetype: VibeArchetype; strength: number }>;
  intensityModifier: number;  // 0.5 = soft, 1.0 = normal, 1.5 = strong
  negations: string[];        // things the user explicitly doesn't want
  novelHints: Partial<Record<string, number>>; // hints for novel dimension adjustments
  detractorOverride: number | null; // explicit detractor preference
  parsedLabel: string;
  confidence: number;         // 0-1 how confident we are in the parse
}

// Synonym map: user word → archetype keyword(s) + novel dimension hints
const SYNONYMS: Record<string, { archetypeKeywords: string[]; novelHints?: Record<string, number> }> = {
  // Lighting
  'warm': { archetypeKeywords: ['warm', 'golden'], novelHints: { emotionalResonance: 0.05 } },
  'soft': { archetypeKeywords: ['soft', 'warm'], novelHints: { authenticityIndex: 0.05 } },
  'dramatic': { archetypeKeywords: ['dramatic'], novelHints: { emotionalResonance: 0.10, depthPerception: 0.05 } },
  'moody': { archetypeKeywords: ['moody', 'dramatic'], novelHints: { emotionalResonance: 0.15 } },
  'bright': { archetypeKeywords: ['clean', 'sharp'] },
  'dark': { archetypeKeywords: ['moody', 'dramatic'] },
  'cinematic': { archetypeKeywords: ['dramatic', 'editorial'], novelHints: { depthPerception: 0.10, colorHarmony: 0.05 } },
  'dreamy': { archetypeKeywords: ['soft', 'warm', 'intimate'], novelHints: { depthPerception: 0.10 } },

  // Composition
  'bokeh': { archetypeKeywords: ['portrait'], novelHints: { depthPerception: 0.15 } },
  'blur': { archetypeKeywords: ['portrait'], novelHints: { depthPerception: 0.10 } },
  'shallow': { archetypeKeywords: ['portrait'], novelHints: { depthPerception: 0.15 } },
  'wide': { archetypeKeywords: ['landscape', 'travel'], novelHints: { depthPerception: 0.10 } },
  'panoramic': { archetypeKeywords: ['landscape', 'travel'] },
  'close-up': { archetypeKeywords: ['portrait', 'headshot'] },
  'macro': { archetypeKeywords: ['sharp'], novelHints: { depthPerception: 0.10 } },
  'symmetry': { archetypeKeywords: ['clean', 'editorial'] },
  'minimal': { archetypeKeywords: ['clean', 'minimal', 'editorial'] },

  // Subject
  'headshot': { archetypeKeywords: ['headshot', 'professional', 'portrait'], novelHints: { gazeDynamics: 0.10 } },
  'portrait': { archetypeKeywords: ['portrait'], novelHints: { gazeDynamics: 0.05 } },
  'selfie': { archetypeKeywords: ['candid', 'portrait'], novelHints: { gazeDynamics: 0.10 } },
  'group': { archetypeKeywords: ['candid'], novelHints: { gazeDynamics: 0.15 } },
  'couple': { archetypeKeywords: ['intimate', 'candid'], novelHints: { gazeDynamics: 0.10, emotionalResonance: 0.10 } },

  // Scene
  'landscape': { archetypeKeywords: ['landscape', 'travel', 'scenery'] },
  'nature': { archetypeKeywords: ['nature', 'travel', 'scenery'] },
  'urban': { archetypeKeywords: ['city', 'modern'], novelHints: { depthPerception: 0.05 } },
  'architecture': { archetypeKeywords: ['architecture', 'travel'] },
  'beach': { archetypeKeywords: ['travel', 'vacation'] },
  'mountain': { archetypeKeywords: ['landscape', 'travel'] },
  'food': { archetypeKeywords: ['close', 'warm'], novelHints: { colorHarmony: 0.10 } },
  'street': { archetypeKeywords: ['candid', 'urban'], novelHints: { authenticityIndex: 0.10 } },

  // Style
  'natural': { archetypeKeywords: ['natural', 'candid', 'authentic'], novelHints: { authenticityIndex: 0.15 } },
  'authentic': { archetypeKeywords: ['authentic', 'candid', 'natural'], novelHints: { authenticityIndex: 0.15 } },
  'raw': { archetypeKeywords: ['authentic', 'candid'], novelHints: { authenticityIndex: 0.20 } },
  'polished': { archetypeKeywords: ['professional', 'editorial', 'polished'] },
  'vibrant': { archetypeKeywords: ['vibrant'], novelHints: { colorHarmony: 0.05 } },
  'muted': { archetypeKeywords: ['soft'], novelHints: { colorHarmony: 0.05 } },
  'vintage': { archetypeKeywords: ['warm', 'cozy'], novelHints: { authenticityIndex: -0.05, colorHarmony: 0.05 } },
  'film': { archetypeKeywords: ['warm'], novelHints: { authenticityIndex: -0.05 } },
  'editorial': { archetypeKeywords: ['editorial', 'clean', 'magazine'] },
  'aesthetic': { archetypeKeywords: ['editorial'], novelHints: { colorHarmony: 0.10, aestheticCohesion: 0.15 } },

  // Emotional
  'happy': { archetypeKeywords: ['candid'], novelHints: { emotionalResonance: 0.10, gazeDynamics: 0.05 } },
  'serious': { archetypeKeywords: ['professional'], novelHints: { emotionalResonance: 0.05 } },
  'fun': { archetypeKeywords: ['candid', 'spontaneous'], novelHints: { emotionalResonance: 0.10 } },
  'elegant': { archetypeKeywords: ['editorial', 'polished'], novelHints: { colorHarmony: 0.10 } },
  'cozy': { archetypeKeywords: ['cozy', 'intimate', 'warm'] },
  'powerful': { archetypeKeywords: ['dramatic'], novelHints: { emotionalResonance: 0.15 } },

  // Quality
  'sharp': { archetypeKeywords: ['sharp', 'professional'] },
  'crisp': { archetypeKeywords: ['sharp', 'clean'] },
  'hd': { archetypeKeywords: ['sharp'] },
  'quality': { archetypeKeywords: ['professional', 'sharp'] },

  // Social Calibration — Dating
  'approachable': { archetypeKeywords: ['candid', 'warm'], novelHints: { emotionalResonance: 0.05, approachabilityWarmth: 0.15 } },
  'friendly': { archetypeKeywords: ['candid', 'warm'], novelHints: { approachabilityWarmth: 0.10 } },
  'adventurous': { archetypeKeywords: ['travel', 'adventure'], novelHints: { lifestyleBreadth: 0.15 } },
  'active': { archetypeKeywords: ['travel', 'adventure'], novelHints: { lifestyleBreadth: 0.10 } },
  'outgoing': { archetypeKeywords: ['candid'], novelHints: { socialProofSignal: 0.10, approachabilityWarmth: 0.05 } },
  'genuine': { archetypeKeywords: ['authentic', 'candid'], novelHints: { authenticityIndex: 0.10, emotionalAvailability: 0.10 } },
  'open': { archetypeKeywords: ['candid'], novelHints: { emotionalAvailability: 0.15, approachabilityWarmth: 0.05 } },
  'stylish': { archetypeKeywords: ['editorial', 'polished'], novelHints: { tastePresentation: 0.15 } },
  'well-dressed': { archetypeKeywords: ['polished'], novelHints: { tastePresentation: 0.15 } },
  'social': { archetypeKeywords: ['candid'], novelHints: { socialProofSignal: 0.15 } },

  // Social Calibration — Instagram
  'eye-catching': { archetypeKeywords: ['vibrant', 'dramatic'], novelHints: { scrollStopPower: 0.15 } },
  'scroll-stopping': { archetypeKeywords: ['dramatic', 'vibrant'], novelHints: { scrollStopPower: 0.20 } },
  'viral': { archetypeKeywords: ['vibrant'], novelHints: { shareabilityIndex: 0.15, scrollStopPower: 0.10 } },
  'shareable': { archetypeKeywords: ['vibrant', 'candid'], novelHints: { shareabilityIndex: 0.15 } },
  'feed': { archetypeKeywords: ['editorial'], novelHints: { aestheticCohesion: 0.15 } },
  'cohesive': { archetypeKeywords: ['editorial', 'clean'], novelHints: { aestheticCohesion: 0.20 } },
  'engaging': { archetypeKeywords: ['candid', 'vibrant'], novelHints: { engagementPredictor: 0.15 } },
  'aspirational': { archetypeKeywords: ['polished', 'editorial'], novelHints: { aspirationalQuality: 0.15 } },

  // Social Calibration — Travel
  'immersive': { archetypeKeywords: ['travel', 'landscape'], novelHints: { sensoryTranslation: 0.15 } },
  'atmospheric': { archetypeKeywords: ['dramatic', 'moody'], novelHints: { sensoryTranslation: 0.15, depthPerception: 0.05 } },
  'storytelling': { archetypeKeywords: ['candid'], novelHints: { storyFragment: 0.15 } },
  'authentic-travel': { archetypeKeywords: ['travel', 'authentic'], novelHints: { iWasThereProof: 0.15 } },
  'experiential': { archetypeKeywords: ['travel', 'candid'], novelHints: { sensoryTranslation: 0.10, storyFragment: 0.10 } },

  // Event / Wedding
  'reception': { archetypeKeywords: ['candid', 'warm', 'intimate'], novelHints: { emotionalResonance: 0.10 } },
  'ceremony': { archetypeKeywords: ['editorial', 'intimate'], novelHints: { emotionalResonance: 0.15 } },
  'toast': { archetypeKeywords: ['candid', 'warm'], novelHints: { emotionalResonance: 0.10 } },
  'dance': { archetypeKeywords: ['candid'], novelHints: { emotionalResonance: 0.10 } },
  'first-dance': { archetypeKeywords: ['intimate', 'warm'], novelHints: { emotionalResonance: 0.15 } },
  'bouquet': { archetypeKeywords: ['close', 'warm'], novelHints: { colorHarmony: 0.10 } },
  'candid': { archetypeKeywords: ['candid', 'authentic'], novelHints: { authenticityIndex: 0.10, emotionalResonance: 0.05 } },
  'celebration': { archetypeKeywords: ['candid', 'warm'], novelHints: { emotionalResonance: 0.10 } },
  'party': { archetypeKeywords: ['candid', 'vibrant'], novelHints: { emotionalResonance: 0.05 } },
  'gathering': { archetypeKeywords: ['candid'], novelHints: { gazeDynamics: 0.10 } },
  'bride': { archetypeKeywords: ['portrait', 'editorial', 'intimate'], novelHints: { emotionalResonance: 0.10 } },
  'groom': { archetypeKeywords: ['portrait', 'editorial'], novelHints: { emotionalResonance: 0.10 } },
  'venue': { archetypeKeywords: ['architecture', 'editorial'], novelHints: { depthPerception: 0.05 } },
  'banquet': { archetypeKeywords: ['warm', 'editorial'], novelHints: { colorHarmony: 0.05 } },
  'wedding': { archetypeKeywords: ['intimate', 'warm', 'editorial'], novelHints: { emotionalResonance: 0.15 } },
  'event': { archetypeKeywords: ['candid', 'warm'], novelHints: { emotionalResonance: 0.05 } },

  // Sports / Action
  'action': { archetypeKeywords: ['dramatic', 'sharp'], novelHints: { depthPerception: 0.05 } },
  'athlete': { archetypeKeywords: ['portrait', 'sharp', 'dramatic'] },
  'stadium': { archetypeKeywords: ['architecture', 'dramatic'] },
  'arena': { archetypeKeywords: ['architecture', 'dramatic'] },
  'game': { archetypeKeywords: ['candid', 'dramatic'], novelHints: { emotionalResonance: 0.05 } },
  'match': { archetypeKeywords: ['dramatic', 'sharp'] },
  'competition': { archetypeKeywords: ['dramatic', 'sharp'], novelHints: { emotionalResonance: 0.10 } },
  'sprint': { archetypeKeywords: ['dramatic'], novelHints: { depthPerception: 0.10 } },
  'goal': { archetypeKeywords: ['dramatic', 'candid'], novelHints: { emotionalResonance: 0.15 } },
  'touchdown': { archetypeKeywords: ['dramatic', 'candid'], novelHints: { emotionalResonance: 0.15 } },
  'finish-line': { archetypeKeywords: ['dramatic'], novelHints: { emotionalResonance: 0.15 } },
  'podium': { archetypeKeywords: ['editorial'], novelHints: { emotionalResonance: 0.10 } },
  'trophy': { archetypeKeywords: ['editorial', 'close'], novelHints: { emotionalResonance: 0.10 } },
  'sideline': { archetypeKeywords: ['candid', 'dramatic'] },
  'panning': { archetypeKeywords: ['dramatic'], novelHints: { depthPerception: 0.10 } },
  'frozen': { archetypeKeywords: ['sharp', 'dramatic'] },
  'peak-moment': { archetypeKeywords: ['dramatic', 'sharp'], novelHints: { emotionalResonance: 0.15 } },
  'victory': { archetypeKeywords: ['candid', 'dramatic'], novelHints: { emotionalResonance: 0.15 } },
};

// Intensity modifiers
const INTENSIFIERS: Record<string, number> = {
  'very': 1.4, 'really': 1.3, 'super': 1.5, 'extremely': 1.6,
  'quite': 1.2, 'slightly': 0.7, 'somewhat': 0.8, 'a bit': 0.8,
  'mostly': 1.1, 'highly': 1.4, 'ultra': 1.5, 'mega': 1.5,
};

// Negation patterns
const NEGATION_PATTERNS = [
  /\b(?:no|without|skip|avoid|exclude|remove|less|fewer|minimal|none)\s+(\w+(?:\s+\w+)?)/gi,
  /\bdon'?t\s+(?:want|include|show|use)\s+(\w+(?:\s+\w+)?)/gi,
  /\bnot?\s+(?:too\s+)?(\w+)/gi,
];

// ─── Main Parser ────────────────────────────────────────────

// Patent: Claim 6 — Natural language vibe-to-weight modulation (v2 parser).
// Enhanced with 120+ synonym vocabulary, intensity modifiers, negation handling,
// and novel dimension hint accumulation. No prior art for NL → scoring weight modulation.
// CLIP does retrieval (not weight modulation). MDIQA does programmatic weights (not NL).
// Adobe US10,521,705 does predefined contexts (not dynamic NL-driven weights).
export function parseVibeV2(input: string, mode?: string): {
  vibeConfig: VibeConfig;
  novelHints: Partial<Record<string, number>>;
  negations: string[];
  confidence: number;
  parsedLabel: string;
} {
  // Guard against regex DoS on extremely long inputs
  const lowered = input.slice(0, 500).toLowerCase().trim();
  if (!lowered) {
    return {
      vibeConfig: { ...DEFAULT_VIBE_CONFIG },
      novelHints: {},
      negations: [],
      confidence: 1.0,
      parsedLabel: 'Balanced scoring',
    };
  }

  const parsed = parseIntent(lowered);

  // Build VibeConfig from archetype matches
  const vibeConfig = buildVibeConfig(parsed, input);

  return {
    vibeConfig,
    novelHints: parsed.novelHints,
    negations: parsed.negations,
    confidence: parsed.confidence,
    parsedLabel: parsed.parsedLabel,
  };
}

function parseIntent(input: string): ParsedIntent {
  const words = tokenize(input);
  const negations = extractNegations(input);
  const intensityModifier = extractIntensity(words);

  // Expand synonyms to archetype keywords
  const expandedKeywords: string[] = [];
  const novelHints: Record<string, number> = {};

  for (const word of words) {
    // Direct archetype keyword match
    for (const archetype of VIBE_ARCHETYPES) {
      if (archetype.keywords.includes(word)) {
        expandedKeywords.push(word);
      }
    }

    // Synonym expansion
    const syn = SYNONYMS[word];
    if (syn) {
      expandedKeywords.push(...syn.archetypeKeywords);
      if (syn.novelHints) {
        for (const [key, val] of Object.entries(syn.novelHints)) {
          novelHints[key] = (novelHints[key] || 0) + val * intensityModifier;
        }
      }
    }
  }

  // Also check multi-word phrases
  for (const [phrase, syn] of Object.entries(SYNONYMS)) {
    if (phrase.includes(' ') || phrase.includes('-')) {
      if (input.includes(phrase)) {
        expandedKeywords.push(...syn.archetypeKeywords);
        if (syn.novelHints) {
          for (const [key, val] of Object.entries(syn.novelHints)) {
            novelHints[key] = (novelHints[key] || 0) + val * intensityModifier;
          }
        }
      }
    }
  }

  // Match archetypes
  const archetypeMatches: Array<{ archetype: VibeArchetype; strength: number }> = [];
  for (const archetype of VIBE_ARCHETYPES) {
    const matchCount = archetype.keywords.filter(
      kw => expandedKeywords.includes(kw) || input.includes(kw)
    ).length;
    if (matchCount > 0) {
      archetypeMatches.push({ archetype, strength: matchCount * intensityModifier });
    }
  }

  // Apply negation adjustments
  let detractorOverride: number | null = null;
  for (const neg of negations) {
    if (neg.includes('crowd') || neg.includes('group') || neg.includes('people')) {
      detractorOverride = 2.0; // strong detractor penalty
    }
    if (neg.includes('filter') || neg.includes('edit') || neg.includes('process')) {
      novelHints['authenticityIndex'] = (novelHints['authenticityIndex'] || 0) + 0.15;
    }
  }

  // Generate label
  const parsedLabel = generateLabel(archetypeMatches, negations);
  const confidence = archetypeMatches.length > 0 ? Math.min(0.95, 0.4 + archetypeMatches.length * 0.15) : 0.3;

  return {
    archetypeMatches,
    intensityModifier,
    negations,
    novelHints,
    detractorOverride,
    parsedLabel,
    confidence,
  };
}

function buildVibeConfig(parsed: ParsedIntent, rawInput: string): VibeConfig {
  if (parsed.archetypeMatches.length === 0) {
    return { ...DEFAULT_VIBE_CONFIG, rawInput, parsedLabel: parsed.parsedLabel };
  }

  const totalStrength = parsed.archetypeMatches.reduce((s, m) => s + m.strength, 0);

  let pDelta = 0, lDelta = 0, cDelta = 0, sDelta = 0;

  for (const { archetype, strength } of parsed.archetypeMatches) {
    const w = strength / totalStrength;
    pDelta += archetype.portraitDelta * w * parsed.intensityModifier;
    lDelta += archetype.lightingDelta * w * parsed.intensityModifier;
    cDelta += archetype.compositionDelta * w * parsed.intensityModifier;
    sDelta += archetype.sceneDelta * w * parsed.intensityModifier;
  }

  const d = DEFAULT_VIBE_CONFIG;
  let pW = Math.max(0.05, d.portraitWeight + pDelta);
  let lW = Math.max(0.05, d.lightingWeight + lDelta);
  let cW = Math.max(0.05, d.compositionWeight + cDelta);
  let sW = Math.max(0.05, d.sceneWeight + sDelta);

  const total = pW + lW + cW + sW;
  pW /= total; lW /= total; cW /= total; sW /= total;

  return {
    rawInput,
    parsedLabel: parsed.parsedLabel,
    portraitWeight: pW,
    lightingWeight: lW,
    compositionWeight: cW,
    sceneWeight: sW,
  };
}

// ─── Text Processing ────────────────────────────────────────

function tokenize(input: string): string[] {
  return input
    .split(/[\s,.!?;:()]+/)
    .filter(Boolean)
    .map(w => w.toLowerCase().replace(/['"]/g, ''));
}

function extractNegations(input: string): string[] {
  const negations: string[] = [];
  for (const pattern of NEGATION_PATTERNS) {
    let match;
    const regex = new RegExp(pattern.source, pattern.flags);
    while ((match = regex.exec(input)) !== null) {
      if (match[1]) negations.push(match[1].toLowerCase().trim());
    }
  }
  return [...new Set(negations)];
}

function extractIntensity(words: string[]): number {
  for (let i = 0; i < words.length; i++) {
    const mod = INTENSIFIERS[words[i]];
    if (mod) return mod;
    // Two-word modifiers
    if (i < words.length - 1) {
      const twoWord = `${words[i]} ${words[i + 1]}`;
      const mod2 = INTENSIFIERS[twoWord];
      if (mod2) return mod2;
    }
  }
  return 1.0;
}

function generateLabel(
  matches: Array<{ archetype: VibeArchetype; strength: number }>,
  negations: string[],
): string {
  if (matches.length === 0) return 'Balanced scoring';

  const sorted = [...matches].sort((a, b) => b.strength - a.strength);
  const primary = sorted[0].archetype.humanLabel;

  if (sorted.length === 1) {
    const suffix = negations.length > 0 ? `, avoiding ${negations[0]}` : '';
    return primary + suffix;
  }

  const secondary = sorted[1].archetype.name;
  const suffix = negations.length > 0 ? ` (no ${negations[0]})` : '';
  return `${primary} with ${secondary} elements${suffix}`;
}
